import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  Computer as ComputerIcon,
  Code as CodeIcon,
  PlayArrow as StartIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';
import candidateTheme from '../../theme/candidateTheme';
import api from '../../api';
import { showSuccess, showError } from '../../utils/sweetAlertConfig';
import CandidateHeader from '../../components/CandidateHeader';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";
const WARNING = "#f39c12";
const INFO = "#3498db";

const CandidateDashboardAssessments = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const candidateData = JSON.parse(localStorage.getItem('candidate_data') || '{}');
      const candidateId = candidateData.candidate_id;
      const token = localStorage.getItem('candidate_token');

      if (!candidateId || !token) {
        navigate('/candidate/login');
        return;
      }

      const response = await api.get(`/candidate/dashboard/${candidateId}`);
      setDashboardData(response.data);

      // Check if birth_date exists (which means SSN was entered)
      // If birth_date is missing, redirect to SSN completion
      if (!response.data.candidate.birth_date) {
        navigate('/candidate/complete-ssn');
        return;
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);

      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        localStorage.removeItem('candidate_token');
        localStorage.removeItem('candidate_data');
        navigate('/candidate/login');
      } else {
        showError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartDigitalAssessment = async (assessment) => {
    try {
      const candidateData = JSON.parse(localStorage.getItem('candidate_data') || '{}');

      const response = await api.post('/candidate/digital-assessment/start', {
        assignment_id: assessment.assignment_id,
        candidate_id: candidateData.candidate_id,
      });

      showSuccess('Assessment started! Good luck!');
      navigate(`/candidate/digital-assessment/${response.data.attempt_id}`);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to start assessment');
    }
  };

  const handleViewTechnicalTask = (assessment) => {
    navigate(`/candidate/technical-task/${assessment.assignment_id}`);
  };

  const renderAssessmentCard = (assessment) => {
    const isDigital = assessment.assessment_type === 'digital_only' || assessment.assessment_type === 'both';
    const isTechnical = assessment.assessment_type === 'technical_only' || assessment.assessment_type === 'both';

    return (
      <Grid item xs={12} key={assessment.assignment_id}>
        <Card
          sx={{
            border: `2px solid ${PRIMARY}`,
            borderRadius: 2,
            '&:hover': { boxShadow: 6 },
            transition: 'all 0.3s',
          }}
        >
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom color={PRIMARY}>
              {assessment.job_title}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Digital Assessment Section */}
            {isDigital && assessment.digital_assessment && (
              <Box mb={isTechnical ? 3 : 0}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ComputerIcon sx={{ color: PRIMARY, fontSize: 30 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Digital Assessment
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    {assessment.digital_assessment.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {assessment.digital_assessment.questions_count} Questions • {assessment.digital_assessment.duration} Minutes
                  </Typography>

                  {assessment.digital_assessment.is_completed && (
                    <Box mt={2} mb={2}>
                      <Alert severity="success" icon={<CompleteIcon />}>
                        <AlertTitle>Completed</AlertTitle>
                        Score: {assessment.digital_assessment.score}%
                      </Alert>
                    </Box>
                  )}

                  {!assessment.digital_assessment.is_completed && (
                    <Button
                      variant="contained"
                      startIcon={<StartIcon />}
                      onClick={() => handleStartDigitalAssessment(assessment)}
                      sx={{
                        bgcolor: PRIMARY,
                        '&:hover': { bgcolor: '#1a1635' },
                        mt: 2,
                        py: 1.5,
                        fontSize: '1rem',
                      }}
                      fullWidth
                    >
                      Start Assessment
                    </Button>
                  )}
                </Paper>
              </Box>
            )}

            {/* Technical Task Section */}
            {isTechnical && assessment.technical_task && (
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CodeIcon sx={{ color: INFO, fontSize: 30 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Technical Task
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    {assessment.technical_task.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Deadline: {new Date(assessment.technical_task.deadline_date).toLocaleDateString()}
                  </Typography>

                  {assessment.technical_task.is_submitted ? (
                    <Box mt={2}>
                      <Alert severity="success">
                        <AlertTitle>Submitted</AlertTitle>
                        {assessment.technical_task.latest_submission?.review_status === 'reviewed' ? (
                          <Box>
                            <Typography variant="body2">
                              Status: Reviewed
                            </Typography>
                            {assessment.technical_task.score && (
                              <Typography variant="body2">
                                Score: {assessment.technical_task.score}%
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            Your submission is under review
                          </Typography>
                        )}
                      </Alert>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => handleViewTechnicalTask(assessment)}
                      sx={{
                        bgcolor: INFO,
                        '&:hover': { bgcolor: '#2980b9' },
                        mt: 2,
                        py: 1.5,
                        fontSize: '1rem',
                      }}
                      fullWidth
                    >
                      View Task & Submit
                    </Button>
                  )}
                </Paper>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!dashboardData) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Container>
          <Alert severity="error">Failed to load dashboard data</Alert>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={candidateTheme}>
      <CandidateHeader candidateName={dashboardData.candidate.full_name} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Simple Welcome */}
        <Paper sx={{ p: 3, mb: 4, background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a1635 100%)`, color: 'white' }}>
          <Typography variant="h4" fontWeight={700}>
            Welcome, {dashboardData.candidate.full_name}!
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
            Complete your assigned assessments below
          </Typography>
        </Paper>

        {/* Assessments List */}
        <Typography variant="h5" fontWeight={600} mb={3} color={PRIMARY}>
          Your Assessments
        </Typography>

        {dashboardData.assessments.length === 0 ? (
          <Alert severity="info">
            <AlertTitle>No Assessments Yet</AlertTitle>
            You don't have any assessments assigned yet. Check back later!
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {dashboardData.assessments.map(renderAssessmentCard)}
          </Grid>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default CandidateDashboardAssessments;
